import { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CARD } from '../../../constants/cards';
import Icon from '../Icon';
import { enableCard, updateCard } from '../../../store/cards';
import {enableFilter} from '../../../store/bookingProcess/entitiesBooking';

/**
 *
 *  This component renders office hours information. If the start time and end time dont have matching date, we show a card with office opening hours.
 *  If the start time and end time match dates, we check if the interval is between office hours of that date.
 *
 * @param {string ISO} props.startTime = start time in ISO format
 * @param {string ISO} props.endTime = end time in ISO format
 * @param {function} props.setIsTimeInsideOfficeHours = setState to remember if the times are inside office hours or not
 * @returns
 */
export function OfficeHoursInfo({ startTime, endTime, setIsTimeInsideOfficeHours }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const selectedOffice = useSelector((state) => state.auth.data.selectedOffice);
	const startTimeObj = DateTime.fromISO(startTime).setZone(selectedOffice?.timezone);
	const endTimeObj = DateTime.fromISO(endTime).setZone(selectedOffice?.timezone);
	const [error, setError] = useState(null);
	const restrictToOfficeHourEnabled = selectedOffice?.teak?.restrictBookingToOfficeHours;

	function openOfficeHoursCard() {
		dispatch(enableCard(true));
		dispatch(updateCard(CARD.VIEW_OFFICE_HOURS));
		dispatch(enableFilter(false));
	}

	useEffect(() => {
		showOfficeHoursInfo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startTime, endTime]);

	function showOfficeHoursInfo() {
		if (!restrictToOfficeHourEnabled) {
			setIsTimeInsideOfficeHours(true);
			return;
		}

		let dateTimeObject = startTimeObj;
		let startWeekday = startTimeObj.weekday - 1;
		const endWeekday = endTimeObj.weekday - 1;
		const allPeriods = [];
		while (startWeekday <= endWeekday) {
			const periods = selectedOffice?.openingHours?.office?.schedule[startWeekday]?.periods;
			const open24Hours = selectedOffice?.openingHours?.office?.schedule[startWeekday]?.open24Hours;
			allPeriods.push(...convertPeriodsInDateTime(getCleanPeriods(periods), dateTimeObject, open24Hours));
			startWeekday++;
			dateTimeObject = dateTimeObject.plus({ days: 1 });
		}

		const cleanTimes = getCleanTimes(allPeriods);

		setIsTimeInsideOfficeHours(isTimeInsideOfficeHours(cleanTimes));
	}

	function isTimeInsideOfficeHours(timeIntervals) {
		let insideOfficeHours = true;
		if (timeIntervals.length === 0) {
			setError(t('bookingPage.outsideOfficeHours.startOusideOfficeHours'));
			insideOfficeHours = false;
		}
		const insideOfficeHoursArray = [];
		for (const interval of timeIntervals) {
			let insideThisInterval = true;
			if (startTimeObj < interval.open || startTimeObj > interval.close) {
				setError(t('bookingPage.outsideOfficeHours.startOusideOfficeHours'));
				insideThisInterval = false;
			} else if (endTimeObj < interval.open || endTimeObj > interval.close) {
				setError(t('bookingPage.outsideOfficeHours.endOusideOfficeHours'));
				insideThisInterval = false;
			}
			insideOfficeHoursArray.push(insideThisInterval);
		}
		insideOfficeHours = insideOfficeHoursArray.some((el) => el);
		if (insideOfficeHours) setError(null);
		return insideOfficeHours;
	}

	return (
		<div className="office-hours">
			{error && <p className="error-info"> {error}</p>}
			<div className="header-wrapper">
				<Icon iconName={'circle-info'} />
				<p>
					<Trans
						i18nKey="bookingPage.outsideOfficeHours.allHoursInfo"
						components={{ span: <span onClick={() => openOfficeHoursCard()} className="redirect" /> }}
					/>
				</p>
			</div>
		</div>
	);
}

//check and clean the list of periods. eg. if there are 2 periods (12:00 - 13:00 / 13:00 - 14:00) the function will return one period 12:00 - 14:00
export function getCleanPeriods(periods) {
	let unsortedPeriods = periods?.slice();
	if (!unsortedPeriods || unsortedPeriods.length === 0) return [];
	let sortedPeriods = unsortedPeriods.sort((a, b) => {
		if (a?.open !== b?.open) return a?.open - b?.open;
		return a?.close - b?.close;
	});
	let resultedPeriods = [];
	let periodsAdded = -1;
	for (let i = 0; i < sortedPeriods.length; i++) {
		const period = sortedPeriods[i];
		const lastPeriodAdded = resultedPeriods[periodsAdded];
		if (period?.open === lastPeriodAdded?.open && period?.close > lastPeriodAdded?.close) {
			resultedPeriods.pop();
			resultedPeriods.push(period);
		} else if (period?.open <= lastPeriodAdded?.close) {
			resultedPeriods.pop();
			resultedPeriods.push({ ...period, open: lastPeriodAdded.open });
		} else {
			resultedPeriods.push(period);
			periodsAdded++;
		}
	}

	return resultedPeriods;
}

//check and clean the list of dateTime. eg. if there are 2 datetimes (12:00 - 13:00 / 13:00 - 14:00) the function will return one datetime 12:00 - 14:00
function getCleanTimes(dateTimes) {
	let resultedCleanTimes = [];
	let cleanTimesAdded = -1;
	for (let i = 0; i < dateTimes.length; i++) {
		const dateTime = dateTimes[i];
		const letTimeAdded = resultedCleanTimes[cleanTimesAdded];
		if (dateTime?.open === letTimeAdded?.open && dateTime?.close > letTimeAdded?.close) {
			resultedCleanTimes.pop();
			resultedCleanTimes.push(dateTime);
		} else if (dateTime?.open <= letTimeAdded?.close) {
			resultedCleanTimes.pop();
			resultedCleanTimes.push({ ...dateTime, open: letTimeAdded.open });
		} else {
			resultedCleanTimes.push(dateTime);
			cleanTimesAdded++;
		}
	}
	return resultedCleanTimes;
}

function convertPeriodsInDateTime(periods, dateTime, open24Hours) {
	if (open24Hours) {
		return [
			{
				open: dateTime.startOf('day'),
				close: dateTime.endOf('day').plus(1),
			},
		];
	}
	if (!periods.length) return [];
	let result = [];

	for (const period of periods) {
		result.push({
			open: dateTime.startOf('day').plus(period.open),
			close: dateTime.startOf('day').plus(period.close),
		});
	}

	return result;
}
